/**
 * PromptStudioDialog — the modal « Prompts et conversations ».
 * Three tabs: Conversations · Prompts · Couverture.
 * Loads: GET .../queries, GET .../allowances, GET .../queries/last-runs; with the conversations
 * feature flag, GET .../personas, .../brand-personas and .../conversations/load (lot I3).
 *
 * @see B-popup-frontend.md step 4, Files and props § PromptStudioDialog
 */
import { useEffect, useRef, useState, useCallback } from 'react';
// Note: useRef used for loadRef sequence tracking
import {
  Dialog,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import type { Brand, Tenant, BrandQuery, BrandQueryAxis, ConversationLoad, ConversationPersona } from '@traaker/shared';
import type { AllowanceFigures, PromptLastRun } from '@traaker/shared';
import { effectiveFeatureFlags } from '@traaker/shared';
import type { PromptAllowanceFigures } from './promptStudioModel';
import { brandQueriesApi } from '../../services/brand-queries';
import { api } from '../../services/api';
import { PromptStudioView } from './PromptStudioView';
import SegmentedControl from '../common/SegmentedControl';
import type { StudioTab } from './studioReducer';
import type { ComposerSource } from './composerModel';
import LaunchDialog from './LaunchDialog';
import type { AgencyFooterFigures, FooterChange } from './FooterSettingsPanel';
import { FiltersButton } from './FiltersButton';
import { EMPTY_FILTER, activeChips } from './filterModel';
import type { FilterState } from './filterModel';
import { EMPTY_CONV_DATA } from './conversations/ConversationsTab';
import type { ConvData } from './conversations/ConversationsTab';
import { EMPTY_CONV_FILTER, conversationsVisible as convTabVisible, convFilterCount } from './conversations/conversationModel';
import type { ConvFilter } from './conversations/conversationModel';
import { conversationsApi } from './conversations/conversationsApi';

export interface PromptStudioDialogProps {
  open: boolean;
  /** Required so a mount cannot forget it: without it every tenant gets the self-serve popup. */
  tenant: Tenant | null;
  brand: Brand;
  initialTab: 'prompts' | 'conversations';
  initialAxis?: BrandQueryAxis;
  /** Lot I3, with `initialTab='conversations'`: 'new' opens the new form, a personaId that conversation's form. */
  initialConversation?: string;
  /** When true, the footer Prompts popover opens immediately. */
  initialFooterOpen?: boolean;
  /** When true, the footer Conversations popover (the conversation cadence) opens immediately. */
  initialConvFooterOpen?: boolean;
  /** Open on the composer with this source (the Search Console OAuth return, lot H). */
  initialSource?: ComposerSource;
  /** Search Console (lot H): isAgency → the band's agency line. */
  searchConsole?: { isAgency: boolean };
  onClose: () => void;
  /** Called ONCE when the popup closes, if anything changed inside it (never per change). */
  onChanged: () => void;
}

export interface LoadState {
  loading: boolean;
  error: boolean;
  queries: BrandQuery[];
  /** A load succeeded since the popup opened (the list is on screen). */
  loaded: boolean;
}

const EMPTY_LOAD: LoadState = { loading: false, error: false, queries: [], loaded: false };

/**
 * A reload once the list is on screen is SILENT: the current data stays (no
 * skeleton, so the opened row, scroll and composer survive) until the new data
 * arrives. Only the first load, or a retry after a failure, shows the skeleton.
 */
export function beginLoad(s: LoadState): LoadState {
  return s.loaded && !s.error ? s : { ...s, loading: true, error: false };
}

type MeteringUsageAgency = {
  agency?: { perBrand: Record<string, { promptCreditsPerMonth: number; nextLaunchAt?: string }>; availableCredits: number };
};

/**
 * The footer's agency balance (lot G step 9). Undefined off agency, or when the
 * readout has no row for this brand: the footer then stays in self-serve mode.
 */
export async function loadAgencyFigures(
  tenant: Pick<Tenant, 'tier'> | null,
  brandId: string | undefined,
  get: (path: string) => Promise<MeteringUsageAgency>,
): Promise<AgencyFooterFigures | undefined> {
  if (tenant?.tier !== 'agency' || !brandId) return undefined;
  const res = await get(`/metering/usage?brandId=${brandId}`);
  const brandUsage = res.agency?.perBrand?.[brandId];
  if (!brandUsage || !res.agency) return undefined;
  return {
    promptCreditsPerMonth: brandUsage.promptCreditsPerMonth,
    nextLaunchAt: brandUsage.nextLaunchAt,
    availableCredits: res.agency.availableCredits,
  };
}

interface FiguresState {
  promptRequests: PromptAllowanceFigures | null;
  conversationRuns: AllowanceFigures | null;
}

export function PromptStudioDialog({
  open,
  tenant,
  brand,
  initialTab,
  initialAxis = 'narrative',
  initialConversation,
  initialFooterOpen,
  initialConvFooterOpen,
  initialSource,
  searchConsole,
  onClose,
  onChanged,
}: PromptStudioDialogProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // `initialTab='conversations'` holds until the allowances answer: a hidden tab then falls back
  // to Prompts (I-D1, resolved after the load, never from a prop guessed before it).
  const [tab, setTab] = useState<StudioTab>(initialTab);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTER);
  const [convFilter, setConvFilter] = useState<ConvFilter>(EMPTY_CONV_FILTER);
  // The conversation routes 403 without the flag: nothing is asked of them then.
  const convFlag = !!tenant && !!effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags).conversationsEnabled;
  const [conv, setConv] = useState<ConvData>(EMPTY_CONV_DATA);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Query load state
  const [queryState, setQueryState] = useState<LoadState>(EMPTY_LOAD);

  // Figures
  const [figures, setFigures] = useState<FiguresState>({
    promptRequests: null,
    conversationRuns: null,
  });

  // Last runs
  const [runs, setRuns] = useState<Record<string, PromptLastRun> | null>(null);

  // Agency budget figures (agency tier only)
  const [agencyUsage, setAgencyUsage] = useState<AgencyFooterFigures | undefined>(undefined);

  // A footer engines write: shown over the page's brand row, which reloads only on close.
  const [footerEngines, setFooterEngines] = useState<string[] | undefined>(undefined);

  // Manual launch dialog (agency only, lot G step 9)
  const [launchDialogOpen, setLaunchDialogOpen] = useState(false);

  const loadRef = useRef(0);
  const convLoadRef = useRef(0);
  // Something changed in the popup: the page behind reloads once, on close.
  const changedRef = useRef(false);

  const loadData = useCallback(async () => {
    if (!brand.brandId) return;
    const seq = ++loadRef.current;

    setQueryState(beginLoad);

    try {
      // Load all in parallel
      const [queryRes, figRes, runsRes] = await Promise.allSettled([
        brandQueriesApi.list(brand.brandId),
        brandQueriesApi.allowances(brand.brandId),
        brandQueriesApi.lastRuns(brand.brandId),
      ]);

      if (seq !== loadRef.current) return;

      const queries: BrandQuery[] =
        queryRes.status === 'fulfilled'
          ? (queryRes.value as BrandQuery[]).filter(
              (q) => !(q.status === 'archived' && q.archivedReason === 'deleted'),
            )
          : [];

      const queryError = queryRes.status === 'rejected';

      setQueryState({ loading: false, error: queryError, queries, loaded: !queryError });

      if (figRes.status === 'fulfilled') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fig = figRes.value as any;
        setFigures({
          promptRequests: fig.promptRequests as PromptAllowanceFigures,
          conversationRuns: fig.conversationRuns ? (fig.conversationRuns as AllowanceFigures) : null,
        });
      }

      if (runsRes.status === 'fulfilled') {
        setRuns(runsRes.value as Record<string, PromptLastRun>);
      }
    } catch {
      if (seq !== loadRef.current) return;
      setQueryState((s) => ({ ...s, loading: false, error: true }));
    }
  }, [brand.brandId]);

  // The Conversations tab's data (lot I3): all three or an error with « Réessayer », never a
  // partial list read as « no conversation ».
  const loadConversations = useCallback(async () => {
    if (!brand.brandId || !convFlag) return;
    const seq = ++convLoadRef.current;
    setConv((s) => (s.loaded && !s.error ? s : { ...s, loading: true, error: false }));
    try {
      const [personas, brandPersonas, load] = await Promise.all([
        conversationsApi.list(brand.brandId),
        conversationsApi.brandPersonas(brand.brandId),
        conversationsApi.load(brand.brandId),
      ]);
      if (seq !== convLoadRef.current) return;
      setConv({ loading: false, error: false, loaded: true, personas: personas ?? [], brandPersonas: brandPersonas ?? [], load });
    } catch {
      if (seq !== convLoadRef.current) return;
      setConv((s) => ({ ...s, loading: false, error: true }));
    }
  }, [brand.brandId, convFlag]);

  // Agency usage figures for the footer balance display (on open and after each change).
  const loadAgency = useCallback(() => {
    loadAgencyFigures(tenant, brand.brandId, (path) => api.get<MeteringUsageAgency>(path))
      .then((figs) => { if (figs) setAgencyUsage(figs); })
      .catch(() => {
        // Non-blocking: footer panel stays in self-serve mode if load fails
      });
  }, [tenant, brand.brandId]);

  // A conversation save answered: the row upserted, the footer on the response's `load`.
  const handleConvSaved = useCallback((persona: ConversationPersona, load: ConversationLoad) => {
    changedRef.current = true;
    setConv((s) => {
      const i = s.personas.findIndex((p) => p.personaId === persona.personaId);
      const personas = i < 0 ? [...s.personas, persona] : s.personas.map((p, j) => (j === i ? persona : p));
      return { ...s, personas, load };
    });
    loadAgency();
  }, [loadAgency]);

  // A deletion answers 204 (no `load`): the figures are read again.
  const handleConvRemoved = useCallback((personaId: string) => {
    changedRef.current = true;
    setConv((s) => ({ ...s, personas: s.personas.filter((p) => p.personaId !== personaId) }));
    conversationsApi.load(brand.brandId).then((load) => setConv((s) => ({ ...s, load })), () => {});
    loadAgency();
  }, [brand.brandId, loadAgency]);

  const handleConvLoad = useCallback((load: ConversationLoad) => {
    changedRef.current = true;
    setConv((s) => ({ ...s, load }));
    loadAgency();
  }, [loadAgency]);

  // Load on open
  useEffect(() => {
    if (open) {
      loadData();
      loadAgency();
      loadConversations();
    } else {
      // Reset on close
      loadRef.current++;
      convLoadRef.current++;
      if (changedRef.current) {
        changedRef.current = false;
        onChanged();
      }
      setQueryState(EMPTY_LOAD);
      setFigures({ promptRequests: null, conversationRuns: null });
      setRuns(null);
      setQ('');
      setFilter(EMPTY_FILTER);
      setConvFilter(EMPTY_CONV_FILTER);
      setConv(EMPTY_CONV_DATA);
      setFiltersOpen(false);
      setTab(initialTab);
      setAgencyUsage(undefined);
      setFooterEngines(undefined);
      setLaunchDialogOpen(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = useCallback(
    (newTab: StudioTab) => {
      setTab(newTab);
    },
    [],
  );

  // An in-popup change: refetch in place, the page behind waits for the close.
  const handleChanged = useCallback(() => {
    changedRef.current = true;
    loadData();
    loadAgency();
  }, [loadData, loadAgency]);

  // A footer write: the cadence comes back with the allowances, the engines from the write.
  const handleFooterSaved = useCallback((change: FooterChange) => {
    if (change.enginesEnabled) setFooterEngines(change.enginesEnabled);
    handleChanged();
  }, [handleChanged]);
  const viewBrand: Brand = footerEngines
    ? { ...brand, auditConfig: { ...brand.auditConfig, enginesEnabled: footerEngines } }
    : brand;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Tab visibility (I-D1): the flag AND `conversationRuns.allowance !== 0` (-1 shows it). Until the
  // allowances answer, a popup opened on Conversations keeps that tab.
  const allowancesKnown = figures.promptRequests !== null;
  const conversationsVisible = convTabVisible(tenant, figures.conversationRuns);
  useEffect(() => {
    if (allowancesKnown && !conversationsVisible && tab === 'conversations') setTab('prompts');
  }, [allowancesKnown, conversationsVisible, tab]);
  const showConvTab = conversationsVisible || (!allowancesKnown && initialTab === 'conversations');

  // Segment options
  const tabOptions = [
    { value: 'conversations' as StudioTab, label: 'Conversations' }, // i18n: promptStudio.tab.conversations
    { value: 'prompts' as StudioTab, label: 'Prompts' },              // i18n: promptStudio.tab.prompts
    { value: 'coverage' as StudioTab, label: 'Couverture' },          // i18n: promptStudio.tab.coverage
  ].filter((o) => o.value !== 'conversations' || showConvTab);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(900px, calc(100vw - 48px))',
          height: 'min(792px, 95vh)',
          borderRadius: isMobile ? 0 : 1.5,
          bgcolor: '#fafafa',
          boxShadow: '0 1px 2px rgba(0,0,0,.03), 0 24px 70px rgba(0,0,0,.22)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          m: 0,
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(11,11,15,.46)' } },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 2.25,
          pt: 1.625,
          pb: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          flexShrink: 0,
        }}
      >
        {/* Title + subtitle */}
        <Box sx={{ flex: '0 0 auto', mr: 2 }}>
          <Box sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.022em', lineHeight: 1.2 }}>
            {/* i18n: promptStudio.title */}
            Prompts et conversations
          </Box>
          <Box sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 322, lineHeight: 1.4, mt: 0.25 }}>
            {/* i18n: promptStudio.subtitle */}
            Questions et conversations menées avec les moteurs IA pour savoir s'ils vous citent.
          </Box>
        </Box>

        {/* Right side: tabs + search + filters + close */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <SegmentedControl
            value={tab}
            options={tabOptions}
            onChange={(v) => handleTabChange(v as StudioTab)}
            ariaLabel="Onglet Prompt Studio" // i18n: promptStudio.tabs.ariaLabel
          />

          {/* Search */}
          <TextField
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher" // i18n: promptStudio.search.placeholder
            inputProps={{ 'aria-label': 'Rechercher une question ou un tag' }}
            sx={{ width: 186, '& .MuiInputBase-root': { fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setQ('')}
                    title="Effacer" // i18n: promptStudio.search.clear
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <FiltersButton
            nFilters={tab === 'conversations' ? convFilterCount(convFilter) : activeChips(filter).length}
            isOpen={filtersOpen && tab !== 'coverage'}
            onClick={() => {
              if (tab === 'coverage') {
                setTab('prompts');
                setFiltersOpen(true);
              } else {
                setFiltersOpen((o) => !o);
              }
            }}
          />

          {/* Close */}
          <IconButton onClick={handleClose} size="small" title="Fermer"> {/* i18n: common.close */}
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <PromptStudioView
          tab={tab}
          brand={viewBrand}
          tenant={tenant ?? undefined}
          queries={queryState.queries}
          loadingQueries={queryState.loading}
          errorQueries={queryState.error}
          promptFigures={figures.promptRequests}
          conversationFigures={figures.conversationRuns}
          runs={runs}
          initialAxis={initialAxis}
          q={q}
          onQ={setQ}
          filter={filter}
          onFilter={setFilter}
          filtersOpen={filtersOpen}
          onCloseFilters={() => setFiltersOpen(false)}
          onChanged={handleChanged}
          onTabChange={handleTabChange}
          agencyFigures={agencyUsage}
          onLaunch={() => setLaunchDialogOpen(true)}
          onFooterSaved={handleFooterSaved}
          initialFooterOpen={initialFooterOpen}
          initialConvFooterOpen={initialConvFooterOpen}
          promptMargin={null}
          initialSource={initialSource}
          searchConsole={searchConsole}
          conversationsVisible={conversationsVisible}
          initialConversation={initialConversation}
          conv={conv}
          convFilter={convFilter}
          onConvFilter={setConvFilter}
          onConvRetry={loadConversations}
          onConvSaved={handleConvSaved}
          onConvRemoved={handleConvRemoved}
          onConvLoad={handleConvLoad}
          onManagePersonas={() => { onClose(); navigate('/strategy#personas'); }}
        />
      </Box>

      {/* Agency brand-wide launch dialog (lot G step 9) */}
      {launchDialogOpen && brand.brandId && (
        <LaunchDialog
          open
          onClose={() => setLaunchDialogOpen(false)}
          brandId={brand.brandId}
          brandName={brand.name}
          onLaunched={handleChanged}
        />
      )}
    </Dialog>
  );
}

export default PromptStudioDialog;
